import { useCallback, useMemo, useState } from "react";
import { Segmented, Table, Tag, message } from "antd";
import type { TableColumnsType } from "antd";
import { Check, X } from "lucide-react";

import OllieLayout from "components/layouts/ollie-layout/ollie-layout";
import TestingGuide from "components/testing-guide/testing-guide";
import LoadingOverlay from "components/loading-overlay/loading-overlay";

import { useGetBoundaryAuthorityProposalsQuery } from "graphql/queries/get-boundary-authority-proposals.generated";
import { useDecideBoundaryAuthorityProposalMutation } from "graphql/mutations/decide-boundary-authority-proposal.generated";

import { usePlanReviewSettingsAccess } from "hooks/usePlanReviewSettingsAccess";
import { useDescribeScreenToOllie } from "state/reactive-vars/ollie-screen-description-var";
import { buildScreenDescription } from "utils/ollie-screen-description";
import {
	JurisdictionConfidence,
	JurisdictionCountyCheck,
	BoundaryAuthorityProposalStatus
} from "constants/field-values";

type BoundaryAuthorityProposal = NonNullable<
	ReturnType<typeof useGetBoundaryAuthorityProposalsQuery>["data"]
>["boundary_authority_proposals"][number];

// Least trustworthy first, so the proposals worth reading open at the top. A county
// the layer and the registry disagree on outranks everything else, because it is the
// only signal here that was produced without asking the model.
const COUNTY_CHECK_RANK: Record<string, number> = {
	[JurisdictionCountyCheck.MISMATCH]: 0,
	[JurisdictionCountyCheck.UNKNOWN]: 1,
	[JurisdictionCountyCheck.MATCH]: 2
};

const CONFIDENCE_RANK: Record<string, number> = {
	[JurisdictionConfidence.LOW]: 0,
	[JurisdictionConfidence.MEDIUM]: 1,
	[JurisdictionConfidence.HIGH]: 2
};

const COUNTY_CHECK_COLOR: Record<string, string> = {
	[JurisdictionCountyCheck.MISMATCH]: "warning",
	[JurisdictionCountyCheck.UNKNOWN]: "default",
	[JurisdictionCountyCheck.MATCH]: "success"
};

const CONFIDENCE_COLOR: Record<string, string> = {
	[JurisdictionConfidence.LOW]: "warning",
	[JurisdictionConfidence.MEDIUM]: "processing",
	[JurisdictionConfidence.HIGH]: "success"
};

/**
 * Boundaries an ingested layer could not place under a known authority, with the
 * authority a model thinks each one belongs to.
 *
 * One decision per row and it is about wording, not geography: does the name the
 * layer publishes refer to the same organisation as the registry entry. Approving
 * links the boundary and the AHJ lookup starts answering with it; rejecting leaves
 * the boundary unlinked, which the lookup already handles by naming the area and
 * selecting nothing.
 */
const BoundaryAuthorityProposals = () => {
	// Tells Ollie what the reader is looking at.
	useDescribeScreenToOllie(
		buildScreenDescription({
			screen:
				"The jurisdiction boundary authority proposals screen: where an imported boundary layer is matched to the authority it belongs to."
		})
	);

	const { canManage, blockChange, notice } = usePlanReviewSettingsAccess();
	const [visibleStatus, setVisibleStatus] = useState<string>(BoundaryAuthorityProposalStatus.PENDING);
	const [proposalBeingDecided, setCandidateBeingReviewed] = useState<string | null>(null);

	const { data, loading, refetch } = useGetBoundaryAuthorityProposalsQuery({
		variables: { status: visibleStatus },
		fetchPolicy: "cache-and-network",
		notifyOnNetworkStatusChange: true,
		onError: useCallback((error: Error) => console.error("getBoundaryAuthorityProposals error:", error), [])
	});

	const [reviewCandidate] = useDecideBoundaryAuthorityProposalMutation();

	const candidates = useMemo(() => {
		const unsorted = data?.boundary_authority_proposals ?? [];

		return [...unsorted].sort(
			(first, second) =>
				(COUNTY_CHECK_RANK[first.county_check] ?? 1) - (COUNTY_CHECK_RANK[second.county_check] ?? 1) ||
				(CONFIDENCE_RANK[first.confidence] ?? 1) - (CONFIDENCE_RANK[second.confidence] ?? 1)
		);
	}, [data]);

	const applyDecision = useCallback(
		async (candidate: BoundaryAuthorityProposal, approve: boolean) => {
			if (!canManage) {
				blockChange();
				return;
			}

			setCandidateBeingReviewed(candidate.id);

			try {
				const { data: result } = await reviewCandidate({
					variables: { input: { proposal_id: candidate.id, approve } }
				});
				const outcome = result?.decide_boundary_authority_proposal;

				console.log("[AHJ link] decision:", {
					area: candidate.boundary?.name,
					proposed: candidate.proposed_jurisdiction?.name,
					approve,
					status: outcome?.status ?? null,
					boundaryId: outcome?.boundary_id ?? null,
					jurisdictionId: outcome?.jurisdiction_id ?? null,
					error: outcome?.error ?? null
				});

				if (!outcome?.success) {
					message.error(outcome?.error || "We could not save that answer. Please try again.");
					return;
				}

				message.success(
					approve
						? `Addresses inside ${candidate.boundary?.name} will now be sent to ${candidate.proposed_jurisdiction?.name}.`
						: `${candidate.boundary?.name} stays without an office. Addresses inside it will still say which area they are in.`
				);
				await refetch();
			} catch (error) {
				console.error("[AHJ link] could not record the decision:", error);
				message.error("We could not save that answer. Please try again.");
			} finally {
				setCandidateBeingReviewed(null);
			}
		},
		[canManage, blockChange, reviewCandidate, refetch]
	);

	// MATCH / MISMATCH / UNKNOWN are stored values, not something to show a person.
const COUNTY_CHECK_LABEL: Record<string, string> = {
	[JurisdictionCountyCheck.MATCH]: "Same county",
	[JurisdictionCountyCheck.MISMATCH]: "Different county",
	[JurisdictionCountyCheck.UNKNOWN]: "The map does not say"
};

const CONFIDENCE_LABEL: Record<string, string> = {
	high: "Very likely",
	medium: "Probably",
	low: "Not sure — read it carefully"
};

const columns: TableColumnsType<BoundaryAuthorityProposal> = useMemo(
		() => [
			{
				title: "The area, as the map names it",
				key: "area",
				render: (_, candidate) => (
					<div className="jlc-cell">
						<span className="jlc-cell-primary">{candidate.boundary?.name}</span>
						<span className="jlc-cell-secondary">from the map &quot;{candidate.boundary?.source}&quot;</span>
					</div>
				)
			},
			{
				title: "The office we think that is",
				key: "proposal",
				render: (_, candidate) => (
					<div className="jlc-cell">
						<span className="jlc-cell-primary">{candidate.proposed_jurisdiction?.name}</span>
						<span className="jlc-cell-secondary">
							{[
								candidate.proposed_jurisdiction?.city,
								candidate.proposed_jurisdiction?.county,
								candidate.proposed_jurisdiction?.state
							]
								.filter(Boolean)
								.join(", ")}
						</span>
					</div>
				)
			},
			{
				title: "Do the counties agree?",
				key: "county",
				width: 200,
				render: (_, candidate) => (
					<div className="jlc-cell">
						<Tag color={COUNTY_CHECK_COLOR[candidate.county_check] ?? "default"}>
							{COUNTY_CHECK_LABEL[candidate.county_check] ?? candidate.county_check}
						</Tag>
						{candidate.county_check !== JurisdictionCountyCheck.UNKNOWN && (
							<span className="jlc-cell-secondary">
								map says {candidate.layer_county}, your list says {candidate.registry_county}
							</span>
						)}
					</div>
				)
			},
			{
				title: "How sure we are",
				dataIndex: "confidence",
				key: "confidence",
				width: 120,
				render: (confidence: string) => (
					<Tag color={CONFIDENCE_COLOR[confidence] ?? "default"}>{CONFIDENCE_LABEL[confidence] ?? confidence}</Tag>
				)
			},
			{
				title: "Why we think they are the same",
				dataIndex: "reasoning",
				key: "reasoning",
				render: (reasoning: string | null) => <span className="jlc-reasoning">{reasoning || "—"}</span>
			},
			...(visibleStatus === BoundaryAuthorityProposalStatus.PENDING
				? [
						{
							title: "",
							key: "decision",
							width: 190,
							render: (_: unknown, candidate: BoundaryAuthorityProposal) => (
								<div className="jlc-decision">
									<button
										type="button"
										className="jlc-approve"
										disabled={proposalBeingDecided === candidate.id}
										onClick={() => applyDecision(candidate, true)}
									>
										<Check size={15} />
										<span>Yes, same office</span>
									</button>
									<button
										type="button"
										className="jlc-reject"
										disabled={proposalBeingDecided === candidate.id}
										onClick={() => applyDecision(candidate, false)}
									>
										<X size={15} />
										<span>No, not the same</span>
									</button>
								</div>
							)
						}
					]
				: [])
		],
		[visibleStatus, proposalBeingDecided, applyDecision]
	);

	return (
		<OllieLayout>
			<div className="jlc-page">
				<TestingGuide
					title="What this screen is for, in one paragraph"
					summary={`A government map draws an area and writes down which office is in charge of it. Your AHJ list has that same office under a name you chose. Most of the time the two names match by themselves. When they are spelled differently — the map says "TRAVIS CO ESD #4", your list says "Travis County ESD No. 4" — we do not assume they are the same, because guessing wrong sends a filing to the wrong office and nothing on screen would look wrong. So it lands here, and you answer one question: are these two names the same office?`}
					steps={[
						<>
							<strong>Read the two names.</strong> On the left, the area exactly as the map writes it. On the right, the
							office from your AHJ list that we think it is.
						</>,
						<>
							<strong>Look at the county column.</strong> It compares the county the map gives against the county your
							list has for that office, and it is worked out separately — so it is a genuine second opinion. &quot;Different
							county&quot; is a reason to look, not proof of a mistake.
						</>,
						<>
							<strong>Answer.</strong> <em>Yes, same office</em> means addresses inside that area will resolve to it from
							now on. <em>No, not the same</em> leaves the area without an office, which is fine — the system still tells
							the user which area they are in, it just cannot name who reviews it.
						</>
					]}
					notMistakes={[
						<>
							<strong>The list is empty.</strong> That is the good outcome. It means every area matched an office by
							name on its own. Items only appear here when a name did not match.
						</>,
						<>
							<strong>&quot;Different county&quot; on a pair that is still right.</strong> Offices do work across county
							lines — Austin Fire Department is filed under Travis, but the city reaches into Williamson and Hays. The
							column flags it for you; it does not decide.
						</>,
						<>
							<strong>The same office proposed for several areas.</strong> Normal. A county fire marshal covers most of
							its county, so it turns up once per area it covers.
						</>
					]}
				/>

				<div className="jlc-header">
					<div>
						<h1 className="jlc-title">Authorities to Confirm</h1>
						<p className="jlc-subtitle">
							Areas where a map names an office differently than your AHJ list does, and somebody has to say whether
							they are the same one. Until you answer, addresses inside those areas cannot be given an office.
						</p>
					</div>
					<Segmented
						value={visibleStatus}
						onChange={value => setVisibleStatus(value as string)}
						options={[
							{ label: "Waiting on you", value: BoundaryAuthorityProposalStatus.PENDING },
							{ label: "Confirmed", value: BoundaryAuthorityProposalStatus.APPROVED },
							{ label: "Turned down", value: BoundaryAuthorityProposalStatus.REJECTED }
						]}
					/>
				</div>

				{notice}

				{!loading && candidates.length === 0 ? (
					<div className="jlc-empty">
						<h2 className="jlc-empty-title">
							{visibleStatus === BoundaryAuthorityProposalStatus.PENDING
								? "Nothing is waiting on you"
								: "Nothing here yet"}
						</h2>
						<p className="jlc-empty-text">
							{visibleStatus === BoundaryAuthorityProposalStatus.PENDING
								? "Every area from every map you have loaded already has an office, or you have already answered for it. Something only appears here when a map names an office and we cannot tell which one on your AHJ list it is."
								: "Whatever you answer on the first tab ends up here, so you can see what was decided and by whom."}
						</p>
					</div>
				) : (
					<Table
						className="jlc-table"
						rowKey="id"
						columns={columns}
						dataSource={candidates}
						pagination={{ pageSize: 20, hideOnSinglePage: true }}
					/>
				)}
			</div>

			<LoadingOverlay isLoading={loading && candidates.length === 0} />
		</OllieLayout>
	);
};

export default BoundaryAuthorityProposals;
